import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_TIMEOUT_MIN = 15;
const EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

/**
 * Hook that signs the user out after `idleTimeoutMinutes` of no activity.
 * Pulls the effective policy from `/api/security/policy/effective` once per mount.
 * Shows a 60s warning toast before logging out.
 */
export function useIdleLogout(onLogout) {
    const lastActivityRef = useRef(Date.now());
    const timeoutRef = useRef(null);
    const warningRef = useRef(null);
    const enabledRef = useRef(true);
    const minutesRef = useRef(DEFAULT_TIMEOUT_MIN);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/security/policy/effective");
                if (cancelled) return;
                enabledRef.current = data.enforce_auto_logout !== false;
                minutesRef.current = Math.max(2, Number(data.idle_timeout_minutes) || DEFAULT_TIMEOUT_MIN);
                schedule();
            } catch {
                schedule();
            }
        };
        const reset = () => {
            lastActivityRef.current = Date.now();
            if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
            schedule();
        };
        const schedule = () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (!enabledRef.current) return;
            const ms = minutesRef.current * 60 * 1000;
            // Warn 60 seconds before auto-logout
            if (warningRef.current) clearTimeout(warningRef.current);
            warningRef.current = setTimeout(() => {
                toast("Signing you out in 60 seconds…", { duration: 60000, id: "idle-warn" });
            }, Math.max(0, ms - 60000));
            timeoutRef.current = setTimeout(() => {
                onLogout?.();
            }, ms);
        };
        EVENTS.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
        load();
        return () => {
            cancelled = true;
            EVENTS.forEach(ev => window.removeEventListener(ev, reset));
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (warningRef.current) clearTimeout(warningRef.current);
        };
    }, [onLogout]);
}
